import { useCallback, useMemo } from 'react'
import { SelectValue } from '@ocean/types'

export type ListItem = Record<string, any>

function getUsedRelatedListsItems(
  assignedItems: Record<string, string | undefined>,
  listOwnerItems: ListItem[],
  listOwnerItemIdKey: string,
  listOwnerItemRelatedListIdKey: string
): Set<string | undefined> {
  const selectedItems = Object.values(assignedItems)
  const receivedItems = listOwnerItems.map(
    ({ [listOwnerItemIdKey]: id, [listOwnerItemRelatedListIdKey]: relatedListId }) =>
      assignedItems.hasOwnProperty(id) ? undefined : relatedListId
  )
  const usedItems = new Set([...receivedItems, ...selectedItems])
  usedItems.delete(undefined)
  return usedItems
}

export type UseRelatedListsProps = {
  listOwnerItems: ListItem[]
  listOwnerItemIdKey?: string
  listOwnerItemRelatedListIdKey: string
  relatedListIdKey?: string
  assignedRelatedListItems: Record<string, string | undefined>
  unassignedRelatedListItemsCount?: number
  onChange: (listOwnerId: string, relatedListItemId?: string) => void
}

export type ListItemData = {
  disabled: boolean
  currentId: string
}

export type RelatedListsData = {
  canBeAddedCount: number
  checkListItemVisibility: (currentId: string, relatedListItem: ListItem) => boolean
  listItemChange: (listOwnerItem: ListItem, newRelatedListItemId: SelectValue) => void
  getListItemData: (listOwnerItem: ListItem) => ListItemData
}

const useRelatedLists = (props: UseRelatedListsProps): RelatedListsData => {
  const {
    assignedRelatedListItems,
    onChange,
    listOwnerItems,
    listOwnerItemIdKey = 'id',
    listOwnerItemRelatedListIdKey,
    unassignedRelatedListItemsCount = 0,
    relatedListIdKey = 'id',
  } = props

  const usedListItemIds = useMemo(() => {
    return getUsedRelatedListsItems(
      assignedRelatedListItems,
      listOwnerItems,
      listOwnerItemIdKey,
      listOwnerItemRelatedListIdKey
    )
  }, [listOwnerItemIdKey, listOwnerItemRelatedListIdKey, assignedRelatedListItems, listOwnerItems])

  const canBeAddedCount = useMemo(() => {
    const assignedItemsCount = new Set(
      Object.values(assignedRelatedListItems).filter(item => !!item)
    ).size
    return unassignedRelatedListItemsCount + assignedItemsCount - listOwnerItems.length
  }, [unassignedRelatedListItemsCount, listOwnerItems.length, assignedRelatedListItems])

  const getListItemData = useCallback(
    (listOwnerItem: ListItem): ListItemData => {
      const { [listOwnerItemIdKey]: id, [listOwnerItemRelatedListIdKey]: oldRelatedListItemId } =
        listOwnerItem
      const currentListItemId = assignedRelatedListItems.hasOwnProperty(id)
        ? assignedRelatedListItems[id]
        : oldRelatedListItemId

      return {
        currentId: currentListItemId,
        disabled: !unassignedRelatedListItemsCount && !currentListItemId,
      }
    },
    [
      listOwnerItemIdKey,
      listOwnerItemRelatedListIdKey,
      assignedRelatedListItems,
      unassignedRelatedListItemsCount,
    ]
  )

  const checkListItemVisibility = useCallback(
    (currentId: string, relatedListItem: ListItem) => {
      const { [relatedListIdKey]: relatedListItemId } = relatedListItem
      return currentId === relatedListItemId || !usedListItemIds.has(relatedListItemId)
    },
    [usedListItemIds, relatedListIdKey]
  )

  const listItemChange = useCallback(
    (listOwnerItem: ListItem, newRelatedListItemId: SelectValue) => {
      const { [listOwnerItemIdKey]: id } = listOwnerItem
      onChange(id, newRelatedListItemId as string)
    },
    [onChange, listOwnerItemIdKey]
  )

  return {
    canBeAddedCount,
    getListItemData,
    checkListItemVisibility,
    listItemChange,
  }
}

export default useRelatedLists
